// Every postback a network sent, accepted or not. Rows show the outcome in words (credited to whom,
// for how much, or why it was refused); expanding one shows the parameters as a table.
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import {
  confirmAction,
  CopyButton,
  EmptyState,
  ErrorNote,
  fmtNum,
  Mono,
  PageHeader,
  Pill,
  SearchInput,
  SkeletonRows,
  StatusPill,
  Tabs,
  TimeAgo,
  toast,
  useDebounced,
  UserChip,
} from '../components/ui';

interface Postback {
  id: string;
  providerId: string;
  kind: string;
  externalTxid: string | null;
  userRef: string | null;
  verified: boolean;
  rejectReason: string | null;
  ip: string | null;
  country: string | null;
  method: string | null;
  query: string | null;
  body: string | null;
  fields: Record<string, string> | null;
  conversionId: string | null;
  receivedAt: number;
  coins: number | null;
  conversionStatus: string | null;
  userId: string | null;
  username: string | null;
}

const KIND: Record<string, { label: string; tone: 'mint' | 'berry' | 'neutral' }> = {
  credit: { label: 'Credit', tone: 'mint' },
  reversal: { label: 'Reversal', tone: 'berry' },
  unknown: { label: 'Unknown status', tone: 'neutral' },
  ban: { label: 'Ban', tone: 'berry' },
};

export function Postbacks() {
  const [params, setParams] = useSearchParams();
  const provider = params.get('provider') ?? '';
  const verified = (params.get('verified') ?? '') as '' | 'true' | 'false';
  const [q, setQ] = useState('');
  const search = useDebounced(q.trim());
  const [open, setOpen] = useState<string | null>(null);
  const qs = new URLSearchParams();
  if (provider) qs.set('provider', provider);
  if (verified) qs.set('verified', verified);
  if (search) qs.set('q', search);
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['postbacks', provider, verified, search],
    queryFn: () =>
      api<{ postbacks: Postback[]; last24h: { accepted: number; rejected: number } }>(
        `/postbacks?${qs}`,
      ),
    refetchInterval: 10_000,
    placeholderData: (p) => p,
  });
  const { data: provs } = useQuery({
    queryKey: ['providers'],
    queryFn: () => api<{ providers: { id: string; name: string }[] }>('/providers'),
  });
  const setParam = (k: string, v: string) =>
    setParams((p) => (v ? p.set(k, v) : p.delete(k), p), { replace: true });

  const names = Object.fromEntries((provs?.providers ?? []).map((p) => [p.id, p.name]));

  return (
    <div className="space-y-5">
      <PageHeader
        title="Postback log"
        description={
          data ? (
            <>
              Last 24 hours:{' '}
              <b className="font-semibold text-ink">{fmtNum(data.last24h.accepted)} accepted</b>,{' '}
              <b
                className={`font-semibold ${data.last24h.rejected ? 'text-berry-deep' : 'text-ink'}`}
              >
                {fmtNum(data.last24h.rejected)} refused
              </b>
              . A postback is the call a network makes to tell us a user finished an offer. Open one
              to see exactly what the network sent.
            </>
          ) : (
            'Every call a network made to tell us a user finished an offer, accepted or refused.'
          )
        }
      />
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <div>
          <Tabs
            label="Outcome"
            value={verified || 'all'}
            onChange={(k) => setParam('verified', k === 'all' ? '' : k)}
            items={[
              { key: 'all', label: 'All' },
              { key: 'true', label: 'Accepted' },
              { key: 'false', label: 'Refused', count: data?.last24h.rejected, hot: true },
            ]}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] md:ml-auto md:flex md:items-center">
          <select
            className="input h-10 py-0 text-[14px] md:w-44"
            value={provider}
            onChange={(e) => setParam('provider', e.target.value)}
            aria-label="Network"
          >
            <option value="">All networks</option>
            {provs?.providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <SearchInput
            value={q}
            onChange={setQ}
            placeholder="Transaction id, user ref or IP"
            className="md:w-64 [&_input]:h-10"
          />
        </div>
      </div>
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {!(error && !data) && (
        <div className="card overflow-hidden">
          {isLoading ? (
            <SkeletonRows />
          ) : !data || data.postbacks.length === 0 ? (
            <EmptyState icon={Activity} title="No postbacks here">
              {search || provider || verified
                ? 'Nothing matches these filters.'
                : 'When a network reports a completed offer, it shows up here within seconds.'}
            </EmptyState>
          ) : (
            <ul className="divide-y divide-hairline">
              {data.postbacks.map((p) => (
                <Row
                  key={p.id}
                  p={p}
                  name={names[p.providerId] ?? p.providerId}
                  open={open === p.id}
                  onToggle={() => setOpen(open === p.id ? null : p.id)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

const sentence = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

function Row({
  p,
  name,
  open,
  onToggle,
}: {
  p: Postback;
  name: string;
  open: boolean;
  onToggle: () => void;
}) {
  const kind = KIND[p.kind] ?? { label: p.kind, tone: 'neutral' as const };
  return (
    <li>
      <button
        onClick={onToggle}
        aria-expanded={open}
        className={`grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 px-4 py-3.5 text-left transition hover:bg-[#fbf8f3] focus-visible:-outline-offset-2 sm:gap-x-4 sm:px-5 ${open ? 'bg-[#fbf8f3]' : ''}`}
      >
        <span
          className={`mt-[7px] h-2.5 w-2.5 rounded-full ${p.verified ? 'bg-mint' : 'bg-berry'}`}
          aria-hidden="true"
        />
        <span className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <span className="sr-only">{p.verified ? 'Accepted:' : 'Refused:'}</span>
          <span className="font-bold">{name}</span>
          <Pill tone={kind.tone}>{kind.label}</Pill>
          {p.verified ? (
            p.kind === 'ban' ? (
              // What applying it did (account blocked, earnings taken back), once done.
              <span className="basis-full text-[13px] text-graphite sm:basis-auto">
                {p.rejectReason ?? 'Accepted, being applied'}
              </span>
            ) : p.coins !== null ? (
              <span className="text-[13px]">
                <b className={`tabular-nums ${p.coins < 0 ? 'text-berry-deep' : 'text-coin-ink'}`}>
                  {p.coins > 0 ? '+' : ''}
                  {fmtNum(p.coins)} coins
                </b>
                {p.username && (
                  <>
                    {' '}
                    {p.coins < 0 ? 'from' : 'to'} <b>{p.username}</b>
                  </>
                )}
              </span>
            ) : (
              <span className="text-[13px] text-stone">Accepted, waiting to be processed</span>
            )
          ) : (
            <span className="basis-full text-[13px] font-semibold text-berry-deep sm:basis-auto">
              {sentence(p.rejectReason ?? 'refused')}
            </span>
          )}
          {p.conversionStatus && <StatusPill status={p.conversionStatus} />}
        </span>
        <span className="flex items-center gap-3 pt-0.5 text-[12px] tabular-nums text-stone">
          <span className="hidden md:inline">
            {p.ip}
            {p.country && <span className="ml-1.5 font-semibold text-graphite">{p.country}</span>}
          </span>
          <span className="whitespace-nowrap text-right sm:min-w-[60px]">
            <TimeAgo t={p.receivedAt} />
          </span>
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={`shrink-0 transition ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {open && <Details p={p} name={name} />}
    </li>
  );
}

function Details({ p, name }: { p: Postback; name: string }) {
  const query = p.query ? [...new URLSearchParams(p.query.replace(/^\?/, ''))] : [];
  let body: string | null = p.body;
  try {
    if (body) body = JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    /* form or text body: show as sent */
  }
  return (
    <div className="space-y-4 border-t border-hairline bg-[#fbf8f3] px-4 py-4 sm:px-5">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px] sm:flex sm:flex-wrap sm:items-start">
        <Meta label="Transaction">{p.externalTxid ? <Mono>{p.externalTxid}</Mono> : '—'}</Meta>
        <Meta label="User ref">{p.userRef ? <Mono>{p.userRef}</Mono> : '—'}</Meta>
        {p.userId && p.username && (
          <Meta label="Credited to">
            <UserChip id={p.userId} username={p.username} />
          </Meta>
        )}
        <Meta label="Method">{p.method ?? '—'}</Meta>
        <Meta label="From">
          {p.ip ?? '—'}
          {p.country && ` · ${p.country}`}
        </Meta>
        <Meta label="Log id">
          <Mono>{p.id}</Mono>
        </Meta>
      </dl>
      {!p.verified && p.kind === 'credit' && p.rejectReason && p.rejectReason in REACCEPTABLE && (
        <AcceptAgain p={p} name={name} what={REACCEPTABLE[p.rejectReason]!} />
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <ParamTable
          title="What we read"
          empty="Nothing read"
          rows={Object.entries(p.fields ?? {})}
        />
        <ParamTable title="Query string as sent" empty="No query string" rows={query} />
      </div>
      {body && (
        <div>
          <div className="mb-1.5 flex items-center gap-2 text-[12px] font-semibold text-stone">
            Body as sent <CopyButton text={p.body ?? ''} label="Copy" />
          </div>
          <pre className="max-h-72 overflow-auto rounded-[12px] border border-hairline bg-paper p-3 text-[12px] leading-relaxed">
            {body}
          </pre>
        </div>
      )}
    </div>
  );
}

/**
 * Refusals that came after every check, for data we couldn't read (packages/core ingest.ts
 * REACCEPTABLE_REASONS): what was missing, and when it can be read.
 */
const REACCEPTABLE: Record<string, { what: string; when: (name: string) => string }> = {
  'missing or invalid amount': {
    what: 'its amount',
    when: (name) =>
      `If ${name}’s setup now reads one (for example the payout when the amount is empty), take it again`,
  },
  'unparseable user id': {
    what: 'its user id',
    when: (name) =>
      `If ${name}’s user id can now be read (for example after it was mapped to another field), take it again`,
  },
};

/**
 * A postback refused only for its amount or user id can be taken again once the network's setup
 * reads it (Networks → Edit → Postback). The member is credited through the usual path, once.
 */
function AcceptAgain({
  p,
  name,
  what,
}: {
  p: Postback;
  name: string;
  what: (typeof REACCEPTABLE)[string];
}) {
  const can = useCan();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<'one' | 'all' | null>(null);
  if (!can('providers.manage')) return null;
  const go = async (all: boolean) => {
    if (
      all &&
      (await confirmAction({
        title: `Accept again every ${name} postback refused for its amount or user id?`,
        body: 'Those from the last 30 days, read with the network’s setup as it is now. Each member is credited once, as if it had come in right; ones the network already sent again count once.',
        confirmLabel: 'Accept them again',
        tone: 'coin',
      })) === null
    )
      return;
    setBusy(all ? 'all' : 'one');
    try {
      const r = await api<{
        considered: number;
        accepted: number;
        duplicate: number;
        refused: { id: string; reason: string }[];
      }>('/postbacks/accept-again', {
        body: { providerId: p.providerId, ...(all ? {} : { ids: [p.id] }) },
      });
      await qc.invalidateQueries({ queryKey: ['postbacks'] });
      if (!all && r.refused[0]) toast(`Not taken: ${r.refused[0].reason}.`, 'error');
      else
        toast(
          `${fmtNum(r.accepted)} accepted${r.duplicate ? `, ${fmtNum(r.duplicate)} already in` : ''}${r.refused.length ? `, ${fmtNum(r.refused.length)} still unreadable` : ''}. Credits follow in a few seconds.`,
        );
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex flex-col gap-3 rounded-[12px] border border-coin/30 bg-coin-tint/50 px-4 py-3 sm:flex-row sm:items-center">
      <p className="min-w-0 flex-1 text-[13px] leading-snug text-graphite">
        <b className="text-ink">Refused for {what.what}.</b> {what.when(name)}: the member is
        credited as usual, once.
      </p>
      <div className="flex shrink-0 flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary h-9 px-3.5 py-0 text-[13px]"
          disabled={!!busy}
          onClick={() => void go(false)}
        >
          {busy === 'one' ? 'Accepting…' : 'Accept again'}
        </button>
        <button
          type="button"
          className="btn-ghost h-9 bg-paper px-3.5 py-0 text-[13px]"
          disabled={!!busy}
          onClick={() => void go(true)}
        >
          {busy === 'all' ? 'Accepting…' : 'All like it (30 days)'}
        </button>
      </div>
    </div>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="mb-0.5 text-[12px] font-semibold text-stone">{label}</dt>
      <dd className="font-semibold tabular-nums">{children}</dd>
    </div>
  );
}

function ParamTable({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: [string, string][];
}) {
  return (
    <div className="min-w-0">
      <div className="mb-1.5 text-[12px] font-semibold text-stone">{title}</div>
      {rows.length === 0 ? (
        <div className="rounded-[12px] border border-dashed border-ink/15 bg-paper px-3.5 py-2.5 text-[13px] text-stone">
          {empty}
        </div>
      ) : (
        <div className="overflow-hidden rounded-[12px] border border-hairline bg-paper">
          <table className="w-full text-[13px]">
            <tbody>
              {rows.map(([k, v], i) => (
                <tr key={`${k}${i}`} className="border-b border-hairline last:border-b-0">
                  <th
                    scope="row"
                    className="w-[1%] whitespace-nowrap px-3.5 py-2 text-left align-top font-mono text-[12px] font-normal text-graphite"
                  >
                    {k}
                  </th>
                  <td className="px-3.5 py-2 align-top font-semibold [overflow-wrap:anywhere]">
                    {v === '' ? <span className="font-normal text-stone">empty</span> : v}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
